import { z } from "zod";

const OfferTypeEnum = z.enum(["Tiny", "Huge"]);
const OfferStatusEnum = z.enum([
  "Planning",
  "Opened",
  "Closed",
  "Accepted",
  "Finished",
  "Payment",
  "PaymentConfirmation",
  "Sending",
  "Receiving",
]);

// Function that takes `t` dynamically
export const createTenderSchema = (t: (key: string) => string) =>
  z
    .object({
      id: z.string().nullable(),
      addressId: z.string().nullable(),
      TenderNr: z.number().nullable(),
      title: z.string().min(1, t("tender.validation.titleRequired")),

      value: z.preprocess(
        (val) => {
          if (val === "" || val === null || typeof val === "undefined") return undefined;
          if (typeof val === "string" && !isNaN(Number(val))) return Number(val);
          if (typeof val === "number") return val;
          return undefined;
        },
        z
          .number({
            required_error: t("quatation.validation.valueRequired"),
            invalid_type_error: t("tender.validation.invalidValue"),
          })
          .positive(t("tender.validation.valuePositive"))
          .optional()
      ),
      companyId: z.string(),
      categoriesIds: z.array(z.string()).optional(),
      fileId: z.union([z.instanceof(File), z.string()]).nullable(),
      endDate: z.string().min(1, t("tender.validation.endDateRequired")),
      openingDate: z
        .string()
        .min(1, t("tender.validation.deliverDateRequired1")),
      type: OfferTypeEnum,
      status: OfferStatusEnum.nullable(),
      region: z.string().min(1, t("company.validation.regionRequired")),
      city: z.string().min(1, t("company.validation.cityRequired")),
      Paylater: z.enum(["Yes", "No"]).default("No"),
      products: z
        .array(
          z.object({
            item: z.string().min(1, t("tender.validation.productNameRequired")),
            quantity: z.preprocess(
              (val) => (val === "" ? undefined : Number(val)),
              z
                .number({
                  required_error: t("quatation.validation.quantityRequired"),
                  invalid_type_error: t("tender.validation.invalidQuantity"),
                })
                .positive(t("tender.validation.quantityPositive"))
            ),
            unit: z.string().optional(),
            notice: z.string().optional(),
            attachment: z.instanceof(File).nullable().optional(),
          })
        )
        .optional(),
    })
    .refine(
      (data) => {
        const endDate = new Date(data.endDate);
        const openingDate = new Date(data.openingDate);
        return (
          !isNaN(endDate.getTime()) &&
          !isNaN(openingDate.getTime()) &&
          openingDate > endDate
        );
      },
      {
        message: t("tender.validation.deliverDateAfterEndDate"),
        path: ["deliverDate"],
      }
    );

export type TenderFormData = z.infer<ReturnType<typeof createTenderSchema>>;
